import React from 'react';
import {
  compose,
  setDisplayName,
  withProps,
  withHandlers,
  withState,
} from 'recompose';
import reactCSS from 'reactcss';
import Button, {
  Kind as ButtonKind,
  Size as ButtonSize,
} from '@ux/button';
import TextButton, {
  Against as TextButtonAgainst,
  Size as TextButtonSize,
} from '@ux/text-button';
import Transition from 'react-inline-transition-group';
import { FormattedMessage } from 'react-intl';
import ReactiveButton, {
  Kind as ReactiveButtonKind,
  Status as ReactiveButtonStatus,
} from '@ux/reactive-button';
import Glyph from '@ux/glyphs';
import { AvailabilityStatus } from '../../../core/constants';
import { getStyles, percentage } from '@ux/style-utils';
import { footnote } from '@ux/type-size';
import { gu2px } from '@ux/theme-utils';
import { whenTrue } from '../../../crosscutting/capra';
import * as r from 'ramda';

const stylesheet = ({
  theme,
}) => {
  const {
    typography,
    spacing,
    palette,
  } = theme;

  return {
    default: {
      root: {
        width: percentage(100),
      },
      availability: {
        fontWeight: 600,
        ...footnote(typography),
      },
      availabilityIcon: {
        display: 'inline-block',
        verticalAlign: 'middle',
        lineHeight: 1,
        marginLeft: gu2px(2, spacing),
      },
      availabilityText: {
        display: 'inline-block',
        verticalAlign: 'middle',
      },
      action: {
        marginTop: gu2px(0.5, spacing),
        marginBottom: gu2px(0.5, spacing),
      },
      unspun: {
        marginTop: 1,
        marginBottom: r.negate(gu2px(0.5, spacing)),
      },
    },
    verifying: {
      availability: {
        color: palette.grey08,
      },
    },

    available: {
      availability: {
        color: palette.grey04,
      },
    },

    unavailable: {
      availability: {
        color: palette.grey02,
      },
    },

    isMinLg: {
      unspun: {
        display: 'flex',
        marginTop: 0,
        marginBottom: 0,
      },
      action: {
        marginRight: gu2px(1, spacing),
        marginBottom: 0,
      },
    },
  };
};

const isStatus = r.propEq('availability');
const isUnverified = isStatus(AvailabilityStatus.UNVERIFIED);
const isAvailable = isStatus(AvailabilityStatus.AVAILABLE);
const isVerifying = isStatus(AvailabilityStatus.VERIFYING);
const isUnavailable = isStatus(AvailabilityStatus.UNAVAILABLE);

export const stylesheetToStyles = (stylesheet, {
  availability,
  isPromo,
  isMinLg,
}) => reactCSS(stylesheet, {
  unverified: isUnverified({ availability }),
  verifying: isVerifying({ availability }),
  available: isAvailable({ availability }),
  unavailable: isUnavailable({ availability }),
  promo: isPromo,
  isMinLg,
});

const ActionButton = compose(
  setDisplayName('ActionButton'),
  withState('status', 'setStatus', ReactiveButton.DEFAULT),
  withHandlers({
    onClick: (props) => () => {
      props.onClick();
      props.setStatus(ReactiveButtonStatus.PENDING);
      setTimeout(function() {
        props.setStatus(ReactiveButtonStatus.SUCCESS);
      }, 1500);
    },
  }),
)(ReactiveButton);

export const Verifying = ({
  styles,
  theme,
  intlNamespace,
}) => (
  <span style={styles.availability}>
    <FormattedMessage id={`dst.${intlNamespace}.verifying`}/>
    <span style={styles.availabilityIcon}>
      <Glyph
        theme={theme}
        size={gu2px(3, theme.spacing)}
        icon="cycle"
        color={theme.palette.grey08}
      />
    </span>
  </span>
);

export const Available = ({
  theme,
  styles,
  intlNamespace,
  onViewBasketClick,
}) => (
  <span style={styles.availability}>
    <span style={styles.availabilityText}>
      <FormattedMessage
        id={`dst.${intlNamespace}.available`}
        values={{
          basketLink: (
            <TextButton
              theme={theme}
              against={TextButtonAgainst.LIGHT}
              size={TextButtonSize.MD}
              onClick={onViewBasketClick}
              data-selector="dst-view-basket"
            >
              <FormattedMessage id={`dst.${intlNamespace}.basket`}/>
            </TextButton>
          ),
        }}
      />
    </span>
    <span style={styles.availabilityIcon}>
      <Glyph
        theme={theme}
        size={gu2px(3, theme.spacing)}
        icon="circleTick"
        color={theme.palette.green01}
      />
    </span>
  </span>
);

export const Unavailable = ({
  styles,
  theme,
  intlNamespace,
}) => (
  <span style={styles.availability}>
    <FormattedMessage id={`dst.${intlNamespace}.unavailable`}/>
    <span style={styles.availabilityIcon}>
      <Glyph
        theme={theme}
        size={gu2px(3, theme.spacing)}
        icon="circleExclamation"
        color={theme.palette.grey02}
      />
    </span>
  </span>
);

export const UnspunUnavailable = ({
  theme,
  fqdn,
  styles,
  intlNamespace,
  canTransfer,
  canBackorder,
  canNegotiate,
  onTransferDomainClick,
  onBackorderDomainClick,
  onNegotiateDomainClick,
}) => (
  <div style={styles.unspun}>
    {whenTrue(
      () => (
        <div style={styles.action}>
          <ActionButton
            theme={theme}
            kind={ReactiveButtonKind.PRIMARY}
            size={ButtonSize.SM}
            block={true}
            onClick={onTransferDomainClick}
            data-selector="dst-domain-transfer"
            data-value={fqdn}
          >
            <FormattedMessage id={`dst.${intlNamespace}.transfer`}/>
          </ActionButton>
        </div>
      )
    )(canTransfer)}

    {whenTrue(
      () => (
        <div style={styles.action}>
          <ActionButton
            theme={theme}
            kind={ReactiveButtonKind.SECONDARY_LIGHT}
            size={ButtonSize.SM}
            block={true}
            onClick={onBackorderDomainClick}
            data-selector="dst-domain-backorder"
            data-value={fqdn}
          >
            <FormattedMessage id={`dst.${intlNamespace}.backorder`}/>
          </ActionButton>
        </div>
      )
    )(canBackorder)}

    {whenTrue(
      () => (
        <div style={styles.action}>
          <Button
            theme={theme}
            kind={ButtonKind.SECONDARY_LIGHT}
            size={ButtonSize.SM}
            block={true}
            onClick={onNegotiateDomainClick}
            data-selector="dst-domain-negotiate"
            data-value={fqdn}
          >
            <FormattedMessage id={`dst.${intlNamespace}.negotiate`}/>
          </Button>
        </div>
      )
    )(canNegotiate)}
  </div>
);

const PureUnverified = ({
  theme,
  fqdn,
  onPurchaseDomainClick,
  block,
  kind,
  intlNamespace,
}) => (
  <Button
    theme={theme}
    kind={kind}
    size={ButtonSize.MD}
    onClick={onPurchaseDomainClick}
    block={block}
    data-selector="dst-domain-buy"
    data-value={fqdn}
  >
    <FormattedMessage id={`dst.${intlNamespace}.buy`}/>
  </Button>
);

export const Unverified = compose(
  setDisplayName('Unverified'),
  withProps((ownerProps) => ({
    kind: ButtonKind.PRIMARY,
    block: r.propEq('isMinSm', false, ownerProps),
  })),
)(PureUnverified);

const animationStyles = {
  base: {
    opacity: 0,
    height: 'auto',
    maxHeight: 0,
    overflow: 'hidden',
  },
  appear: {
    opacity: 1,
    maxHeight: 150,
  },
  enter: {
    opacity: 1,
    maxHeight: 150,
    transition: 'max-height 0s ease 0.2s, opacity 0.3s ease-in-out 0.2s',
  },
  leave: {
    opacity: 0,
    maxHeight: 150,
    transition: 'opacity 0.2s ease-in-out',
  },
};

export const PureDomainPurchase = (props) => (
  <Transition
    style={props.styles.root}
    childrenStyles={animationStyles}
  >
    {r.cond([
      [ isVerifying, (props) => (
        <div key="verifying">
          <Verifying {...props}/>
        </div>
      ) ],
      [ isAvailable, (props) => (
        <div key="available">
          <Available {...props}/>
        </div>
      ) ],
      [ isUnavailable, (props) => (
        <div key="unavailable">
          {r.ifElse(
            () => r.and(props.canTransferOrBackorder, props.unspun),
            (props) => <UnspunUnavailable {...props}/>,
            (props) => <Unavailable {...props}/>
          )(props)}
        </div>
      ) ],
      [ isUnverified, (props) => (
        <div key="unverified">
          <Unverified {...props}/>
        </div>
      ) ],
      [ r.T, <div key="null"/> ],
    ])(props)}
  </Transition>
);

export const enhance = compose(
  setDisplayName('DomainPurchase'),
  withHandlers({
    onViewBasketClick: ({ onViewBasketClick }) => () => {
      onViewBasketClick('suggestion');
    },
  }),
  withProps((ownerProps) => ({
    styles: getStyles(stylesheet, stylesheetToStyles, ownerProps),
    canTransferOrBackorder: r.either(
      r.propEq('canTransfer', true),
      r.propEq('canBackorder', true)
    )(ownerProps),
  }))
);

export default enhance(PureDomainPurchase);
